/**
 * What the numbers are allowed to claim.
 *
 * Pure and separate from the screen for the same reason `isDayGood` lives apart
 * from the streak walk: these three rules decide what a learner is told about
 * their own grasp of a rule, and two of them are thresholds that are only
 * correct or incorrect against examples.
 *
 * The rule that matters most is the refusal. A lesson answered twice, both
 * right, is not 100% — it is unmeasured, and rendering it as a percentage makes
 * a guess indistinguishable from a record. `recentAccuracy` returns null there
 * and the screen prints the count instead.
 */
import type { GrammarLessonScore } from './types';

/**
 * Answers below which no percentage is shown.
 *
 * Not a statistical threshold — an honesty one. A lesson holds eight to twelve
 * exercises and one daily session touches six, so five answers is roughly "you
 * have sat with this rule once".
 */
export const MIN_JUDGEABLE = 5;

/** Below this, recent accuracy is worth acting on rather than celebrating. */
export const SHAKY = 0.7;

/**
 * How the lesson is going NOW, or null when too little has been answered.
 *
 * Recent rather than lifetime because lifetime is dominated by the day the rule
 * was new, when the learner was by definition at their worst — a lesson that is
 * now solid would read 62% forever.
 */
export function recentAccuracy(score: GrammarLessonScore | null): number | null {
  if (!score || score.recent_attempts < MIN_JUDGEABLE) return null;
  return score.recent_correct / score.recent_attempts;
}

/**
 * Does this lesson need work?
 *
 * Two independent reasons, and the flag counts even when the accuracy does not:
 * `struggling` is set per exercise after a run of consecutive misses, so a
 * lesson can sit at a respectable average while one exercise in it wins every
 * time. That exercise is the reason to come back.
 */
export function isShaky(score: GrammarLessonScore | null): boolean {
  if (!score) return false;
  if (score.struggling > 0) return true;
  const recent = recentAccuracy(score);
  return recent !== null && recent < SHAKY;
}

/** At or below this many stars, the learner has said the rule did not land. */
export const SELF_SHAKY = 2;

/**
 * What each point on the scale means.
 *
 * Written down rather than left to the learner's sense of the day, because an
 * unlabelled 1-to-5 drifts: the same rule that felt like a 2 in September gets
 * a 2 again in October for a completely different reason, and the comparison
 * the rating exists for quietly stops meaning anything.
 */
export const STAR_LABELS: Record<number, string> = {
  1: 'Did not land',
  2: 'Shaky',
  3: 'Getting there',
  4: 'Comfortable',
  5: 'Got it',
};

/**
 * The two independent reasons a lesson can want work, kept apart on purpose.
 *
 * `measured` is what the attempts say. `self` is what the learner says. The
 * interesting rows are the ones where they disagree, and folding them into a
 * single boolean would destroy exactly that: five stars against 58% is a blind
 * spot, two stars against 95% is a rule already in hand and not yet trusted,
 * and a screen that only prints "needs work" cannot tell you which you are
 * looking at.
 *
 * Either one alone is enough to flag. The learner's own verdict is not a weaker
 * signal than the arithmetic — on a rule they have to produce in conversation
 * tomorrow, it is usually the better one.
 */
export function workSignals(
  score: GrammarLessonScore | null,
  stars: number | null,
): { measured: boolean; self: boolean } {
  return {
    measured: isShaky(score),
    self: stars !== null && stars <= SELF_SHAKY,
  };
}

/** Does this lesson want work, by either signal? */
export function needsWork(score: GrammarLessonScore | null, stars: number | null): boolean {
  const { measured, self } = workSignals(score, stars);
  return measured || self;
}

/**
 * Sort key for "weakest first". Lower sorts earlier.
 *
 * The worse of the two signals wins, each mapped onto 0..1 — a lesson you rated
 * one star belongs at the top whatever its accuracy, and a lesson scoring 40%
 * belongs there whatever you rated it. Taking the minimum rather than an
 * average is deliberate: averaging lets a confident five stars bury a measured
 * failure, which is the blind spot this list exists to surface.
 *
 * A lesson with neither signal sorts AFTER every lesson that has one. It is not
 * known to be weak, and putting unmeasured rules above measured bad ones would
 * send the learner to the wrong lesson — the one thing a "weakest first" list
 * must not do.
 */
export function weakestFirstRank(
  score: GrammarLessonScore | null,
  stars: number | null = null,
): number {
  const signals: number[] = [];
  const recent = recentAccuracy(score);
  if (recent !== null) signals.push(recent);
  // 1 star -> 0, 5 stars -> 1, so both axes read "higher is better".
  if (stars !== null) signals.push((stars - 1) / 4);
  return signals.length === 0 ? 2 : Math.min(...signals);
}
